import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  Button,
  TextField,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useGuest } from '../context/GuestContext';
import { getGuestCourse, getGuestCourseTopics, recordGuestTopicAccess, updateGuestEmail } from '../services/guestApi';
import type { CourseDTO, CourseTopicDTO } from '../services/guestApi';
import { trackGuestEvent } from '../services/guestAnalyticsService';

/**
 * Guest course view: shows course and up-to-2 allowed topics.
 * Guest can open a topic (records access); sign-up CTA for full access.
 */
const GuestCourseView: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { guestId, ensureSession } = useGuest();
  const [course, setCourse] = useState<CourseDTO | null>(null);
  const [topics, setTopics] = useState<CourseTopicDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessingTopicId, setAccessingTopicId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const id = courseId ? Number(courseId) : NaN;
    if (!Number.isFinite(id)) {
      setError('Invalid course');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const gid = await ensureSession();
        if (cancelled) return;
        const [courseRes, topicsRes] = await Promise.all([
          getGuestCourse(gid, id),
          getGuestCourseTopics(gid, id),
        ]);
        if (cancelled) return;
        setCourse(courseRes);
        setTopics(topicsRes);
        trackGuestEvent(gid, {
          eventType: 'topic_list_viewed',
          courseId: id,
          metadata: { topic_count_shown: topicsRes.length },
        });
      } catch (e: unknown) {
        if (!cancelled) {
          const err = e as { response?: { data?: { message?: string } }; message?: string };
          const message = err.response?.data?.message ?? (e instanceof Error ? e.message : 'Failed to load course');
          setError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, ensureSession]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !guestId) return;
    setEmailSubmitting(true);
    try {
      await updateGuestEmail(guestId, trimmed);
      setEmailSubmitted(true);
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleTopicClick = async (topicId: number, guestAccessible?: boolean) => {
    if (guestAccessible === false) {
      setError('Sign up to access more topics. Guests can open up to 2 topics per visit.');
      return;
    }
    const gid = guestId ?? localStorage.getItem('guestId');
    if (!gid || !courseId) return;
    setAccessingTopicId(topicId);
    setError(null);
    try {
      await recordGuestTopicAccess(gid, topicId, Number(courseId));
      navigate(`/guest/topic/${topicId}`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; status?: number } };
      const msg =
        err.response?.data?.message ??
        (err.response?.status === 403
          ? 'You have reached the guest limit of 2 topics. Sign up for full access.'
          : 'Could not open this topic. Please try again or sign up.');
      setError(msg);
    } finally {
      setAccessingTopicId(null);
    }
  };

  if (error || !course) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error ?? 'Course not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/guest/available')}>
          Back to course selection
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          {course.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You're browsing as a guest. You can access up to 2 topics, 2 quizzes, and 1 assessment.
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          You're viewing up to 2 topics. Sign up to access the full course.
          <Button component={Link} to="/signup" variant="contained" size="small" sx={{ ml: 2 }}>
            Sign up
          </Button>
        </Alert>
        <Box sx={{ mb: 3 }}>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
          >
            Register for this course
          </Button>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Sign up to enrol in this course and save your progress.
          </Typography>
        </Box>

        {!emailSubmitted && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Get updates (optional)
            </Typography>
            <form onSubmit={handleEmailSubmit}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <Button type="submit" variant="outlined" size="small" disabled={emailSubmitting || !email.trim()}>
                  {emailSubmitting ? 'Submitting…' : 'Submit'}
                </Button>
              </Box>
            </form>
          </Paper>
        )}
        {emailSubmitted && (
          <Alert severity="success" sx={{ mb: 2 }}>Thanks! We'll keep you updated.</Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>
          Topics ({topics.filter((t) => t.guestAccessible !== false).length} of 2 available as guest
          {topics.length > 0 ? ` · ${topics.length} in course` : ''})
        </Typography>
        {topics.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
            No topics available for this course in guest mode.
          </Typography>
        ) : (
          <List>
            {topics.map((t) => {
              const canOpen = t.guestAccessible !== false;
              return (
                <ListItem key={t.topicId} disablePadding>
                  <ListItemButton
                    onClick={() => handleTopicClick(t.topicId, t.guestAccessible)}
                    disabled={accessingTopicId === t.topicId || !canOpen}
                  >
                    <ListItemText
                      primary={t.topicTitle}
                      secondary={
                        canOpen
                          ? t.topicDescription
                          : `${t.topicDescription ? `${t.topicDescription} · ` : ''}Sign up to unlock`
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/guest/available')}>
            Back to course selection
          </Button>
          <Button component={Link} to="/signup" variant="contained" color="primary">
            Sign up for full access
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestCourseView;
